import type { PixelationPipeline } from '../render/Pixelation';
import { motionBlurPrefs } from '../render/MotionBlurPrefs';
import { attachMenuSounds, menuSounds } from './MenuSounds';
import { gameAudio } from '../audio/GameAudio';
import { leftyLayout } from './LeftyLayout';
import { controlMode, type ControlMode } from './ControlMode';
import { keyboardDriveMode, type KeyboardDriveMode } from './KeyboardDriveMode';
import { musicMute } from './MusicMute';
import { musicVolume } from './MusicVolume';
import {
  readRendererBackendPreference,
  setRendererBackendPreference,
  type RendererBackendPreference,
} from '../render/Renderer';
import {
  __test__isBrakeKey as isBrakeKey,
  __test__isThrottleKey as isThrottleKey,
} from '../game/Input';

export interface SettingsHandlers {
  onClose: () => void;
  onRendererBackendChange?: () => void;
}

export class SettingsScreen {
  readonly root: HTMLDivElement;
  private readonly pressedKeys = new Set<string>();
  private readonly comboLeftEl: HTMLSpanElement;
  private readonly comboRightEl: HTMLSpanElement;

  constructor(parent: HTMLElement, pixelation: PixelationPipeline, handlers: SettingsHandlers) {
    this.root = document.createElement('div');
    this.root.id = 'settings-screen';
    this.root.className = 'tst-screen tst-overlay';
    this.root.style.zIndex = '35';
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="tst-card tst-card-wide tst-card-tall">
        <div class="tst-title-sm">SETTINGS</div>

        <div class="tst-card-scroll">
          <div class="tst-row">
            <label>Pixelation</label>
            <input type="checkbox" data-id="pix-enabled" />
          </div>
          <div class="tst-row">
            <label>Pixel resolution</label>
            <div>
              <input type="range" data-id="pix-res" min="120" max="960" step="20" />
              <span data-id="pix-res-value" class="tst-num"></span>
            </div>
          </div>
          <div class="tst-row">
            <label>Motion blur</label>
            <input type="checkbox" data-id="motion-blur-enabled" />
          </div>
          <div class="tst-row">
            <label>Motion blur strength</label>
            <div>
              <input type="range" data-id="motion-blur-intensity" min="0" max="100" step="1" />
              <span data-id="motion-blur-intensity-value" class="tst-num"></span>
            </div>
          </div>
          <div class="tst-row">
            <label>Fullscreen</label>
            <button data-action="fullscreen" class="tst-btn tst-btn-sm">Toggle</button>
          </div>
          <div class="tst-row">
            <label>Renderer</label>
            <select data-id="renderer-backend" class="tst-select">
              <option value="auto">Auto (desktop WebGPU)</option>
              <option value="webgpu">WebGPU</option>
              <option value="webgl2">WebGL</option>
            </select>
          </div>
          <div class="tst-row">
            <label>Left-handed layout</label>
            <input type="checkbox" data-id="lefty-layout" />
          </div>
          <div class="tst-row tst-row-touch-only" data-id="row-control-mode">
            <label>Mobile controls</label>
            <select data-id="control-mode" class="tst-select">
              <option value="buttons">Buttons (steer + boost/brake)</option>
              <option value="sticks">Sticks (per-engine thrust)</option>
            </select>
          </div>
          <div class="tst-row tst-row-keyboard-only" data-id="row-keyboard-drive-mode">
            <label>Keyboard driving</label>
            <select data-id="keyboard-drive-mode" class="tst-select">
              <option value="wasd">WASD (throttle + steer + brake)</option>
              <option value="differential">Q/E + Space (per-engine + e-brake)</option>
            </select>
          </div>
          <div class="tst-keyboard-panel" aria-label="Keyboard controls">
            <div class="tst-card-label">Keyboard Controls</div>
            <div data-id="keyboard-wasd" class="tst-keyboard-line">WASD and arrow keys both drive.</div>
            <div data-id="keyboard-alt-brake" class="tst-keyboard-line">Brake: S, Space, or Down.</div>
            <div class="tst-keyboard-line">Pause: Esc or P. Camera: C or V.</div>
            <div class="tst-keyboard-combos">
              <div class="tst-keyboard-combo">
                <span>Throttle + Left + Brake</span>
                <span data-id="combo-left" class="tst-keyboard-status">Hold W + A + Space</span>
              </div>
              <div class="tst-keyboard-combo">
                <span>Throttle + Right + Brake</span>
                <span data-id="combo-right" class="tst-keyboard-status">Hold W + D + Space</span>
              </div>
            </div>
          </div>
          <div class="tst-section-label">Sound</div>
          <div class="tst-row">
            <label>Music</label>
            <input type="checkbox" data-id="music-enabled" />
          </div>
          <div class="tst-row">
            <label>Music volume</label>
            <div>
              <input type="range" data-id="music-volume" min="0" max="100" step="1" />
              <span data-id="music-volume-value" class="tst-num"></span>
            </div>
          </div>
          <div class="tst-row">
            <label>UI volume</label>
            <div>
              <input type="range" data-id="ui-volume" min="0" max="100" step="1" />
              <span data-id="ui-volume-value" class="tst-num"></span>
            </div>
          </div>
          <div class="tst-row">
            <label>Engine + SFX</label>
            <input type="checkbox" data-id="audio-enabled" />
          </div>
          <div class="tst-row">
            <label>Master volume</label>
            <div>
              <input type="range" data-id="audio-master" min="0" max="100" step="1" />
              <span data-id="audio-master-value" class="tst-num"></span>
            </div>
          </div>
          <div class="tst-row">
            <label>Engine volume</label>
            <div>
              <input type="range" data-id="audio-engine" min="0" max="100" step="1" />
              <span data-id="audio-engine-value" class="tst-num"></span>
            </div>
          </div>
          <div class="tst-row">
            <label>SFX volume</label>
            <div>
              <input type="range" data-id="audio-sfx" min="0" max="100" step="1" />
              <span data-id="audio-sfx-value" class="tst-num"></span>
            </div>
          </div>
        </div>

        <div class="tst-buttons" style="margin-top:14px;">
          <button data-action="close" class="tst-btn tst-btn-primary">Done</button>
        </div>
      </div>
    `;

    const pixEnabled = this.root.querySelector('[data-id="pix-enabled"]') as HTMLInputElement;
    const pixRes = this.root.querySelector('[data-id="pix-res"]') as HTMLInputElement;
    const pixResValue = this.root.querySelector('[data-id="pix-res-value"]') as HTMLSpanElement;
    const uiVolume = this.root.querySelector('[data-id="ui-volume"]') as HTMLInputElement;
    const uiVolumeValue = this.root.querySelector('[data-id="ui-volume-value"]') as HTMLSpanElement;
    this.comboLeftEl = this.root.querySelector('[data-id="combo-left"]') as HTMLSpanElement;
    this.comboRightEl = this.root.querySelector('[data-id="combo-right"]') as HTMLSpanElement;

    pixEnabled.checked = pixelation.config.enabled;
    pixRes.value = String(pixelation.config.width);
    pixResValue.textContent = `${pixelation.config.width}×${pixelation.config.height}`;
    uiVolume.value = String(Math.round(menuSounds.getVolume() * 100));
    uiVolumeValue.textContent = `${uiVolume.value}%`;

    pixEnabled.addEventListener('change', () => {
      pixelation.config.enabled = pixEnabled.checked;
    });
    pixRes.addEventListener('input', () => {
      const w = Number(pixRes.value);
      const h = Math.round((w * 9) / 16);
      pixelation.setResolution(w, h);
      pixResValue.textContent = `${w}×${h}`;
    });
    uiVolume.addEventListener('input', () => {
      menuSounds.setVolume(Number(uiVolume.value) / 100);
      uiVolumeValue.textContent = `${uiVolume.value}%`;
    });

    // Motion blur toggle + intensity. Persisted via motionBlurPrefs singleton;
    // main.ts reads the live values per-frame.
    const motionBlurEnabled = this.root.querySelector('[data-id="motion-blur-enabled"]') as HTMLInputElement;
    const motionBlurIntensity = this.root.querySelector('[data-id="motion-blur-intensity"]') as HTMLInputElement;
    const motionBlurIntensityValue = this.root.querySelector('[data-id="motion-blur-intensity-value"]') as HTMLSpanElement;
    const refreshMotionBlur = (): void => {
      motionBlurEnabled.checked = motionBlurPrefs.isEnabled();
      const pct = Math.round(motionBlurPrefs.getIntensity() * 100);
      motionBlurIntensity.value = String(pct);
      motionBlurIntensityValue.textContent = `${pct}%`;
      motionBlurIntensity.disabled = !motionBlurEnabled.checked;
    };
    refreshMotionBlur();
    motionBlurEnabled.addEventListener('change', () => {
      motionBlurPrefs.setEnabled(motionBlurEnabled.checked);
      refreshMotionBlur();
    });
    motionBlurIntensity.addEventListener('input', () => {
      const pct = Number(motionBlurIntensity.value);
      motionBlurPrefs.setIntensity(pct / 100);
      motionBlurIntensityValue.textContent = `${pct}%`;
    });
    this.refreshMotionBlur = refreshMotionBlur;

    const leftyToggle = this.root.querySelector('[data-id="lefty-layout"]') as HTMLInputElement;
    leftyToggle.checked = leftyLayout.isEnabled();
    leftyToggle.addEventListener('change', () => {
      leftyLayout.setEnabled(leftyToggle.checked);
    });

    const controlModeSelect = this.root.querySelector('[data-id="control-mode"]') as HTMLSelectElement;
    controlModeSelect.value = controlMode.get();
    controlModeSelect.addEventListener('change', () => {
      controlMode.set(controlModeSelect.value as ControlMode);
    });

    const keyboardDriveSelect = this.root.querySelector('[data-id="keyboard-drive-mode"]') as HTMLSelectElement;
    keyboardDriveSelect.value = keyboardDriveMode.get();
    keyboardDriveSelect.addEventListener('change', () => {
      keyboardDriveMode.set(keyboardDriveSelect.value as KeyboardDriveMode);
    });

    const rendererBackendSelect = this.root.querySelector('[data-id="renderer-backend"]') as HTMLSelectElement;
    rendererBackendSelect.value = readRendererBackendPreference();
    rendererBackendSelect.addEventListener('change', () => {
      setRendererBackendPreference(rendererBackendSelect.value as RendererBackendPreference);
      (handlers.onRendererBackendChange ?? reloadWithoutRendererOverrideParams)();
    });

    // Audio: engine + sfx toggle and three volume sliders. Reflected back
    // to/from the GameAudio singleton; localStorage persistence is handled
    // there so reopening Settings shows the current values.
    const musicEnabled = this.root.querySelector('[data-id="music-enabled"]') as HTMLInputElement;
    const musicVolumeInput = this.root.querySelector('[data-id="music-volume"]') as HTMLInputElement;
    const musicVolumeValue = this.root.querySelector('[data-id="music-volume-value"]') as HTMLSpanElement;
    const audioEnabled = this.root.querySelector('[data-id="audio-enabled"]') as HTMLInputElement;
    const audioMaster = this.root.querySelector('[data-id="audio-master"]') as HTMLInputElement;
    const audioMasterValue = this.root.querySelector('[data-id="audio-master-value"]') as HTMLSpanElement;
    const audioEngine = this.root.querySelector('[data-id="audio-engine"]') as HTMLInputElement;
    const audioEngineValue = this.root.querySelector('[data-id="audio-engine-value"]') as HTMLSpanElement;
    const audioSfx = this.root.querySelector('[data-id="audio-sfx"]') as HTMLInputElement;
    const audioSfxValue = this.root.querySelector('[data-id="audio-sfx-value"]') as HTMLSpanElement;

    const refreshAudioRows = (): void => {
      musicEnabled.checked = !musicMute.isMuted();
      musicVolumeInput.value = String(Math.round(musicVolume.get() * 100));
      musicVolumeValue.textContent = `${musicVolumeInput.value}%`;
      audioEnabled.checked = !gameAudio.isMuted();
      audioMaster.value = String(Math.round(gameAudio.getMasterVolume() * 100));
      audioMasterValue.textContent = `${audioMaster.value}%`;
      audioEngine.value = String(Math.round(gameAudio.getEngineVolume() * 100));
      audioEngineValue.textContent = `${audioEngine.value}%`;
      audioSfx.value = String(Math.round(gameAudio.getSfxVolume() * 100));
      audioSfxValue.textContent = `${audioSfx.value}%`;
    };
    refreshAudioRows();
    musicEnabled.addEventListener('change', () => {
      musicMute.setMuted(!musicEnabled.checked);
    });
    musicVolumeInput.addEventListener('input', () => {
      musicVolume.set(Number(musicVolumeInput.value) / 100);
      musicVolumeValue.textContent = `${musicVolumeInput.value}%`;
    });
    audioEnabled.addEventListener('change', () => {
      gameAudio.setMuted(!audioEnabled.checked);
    });
    audioMaster.addEventListener('input', () => {
      gameAudio.setMasterVolume(Number(audioMaster.value) / 100);
      audioMasterValue.textContent = `${audioMaster.value}%`;
    });
    audioEngine.addEventListener('input', () => {
      gameAudio.setEngineVolume(Number(audioEngine.value) / 100);
      audioEngineValue.textContent = `${audioEngine.value}%`;
    });
    audioSfx.addEventListener('input', () => {
      gameAudio.setSfxVolume(Number(audioSfx.value) / 100);
      audioSfxValue.textContent = `${audioSfx.value}%`;
    });
    // Repopulate every time the screen opens so an external mute toggle
    // (the AudioToggle button) is reflected here.
    this.refreshAudioRows = refreshAudioRows;

    this.root.querySelector('[data-action="fullscreen"]')!.addEventListener('click', () => {
      if (document.fullscreenElement) {
        document.exitFullscreen?.();
      } else {
        document.documentElement.requestFullscreen?.();
      }
    });
    this.root.querySelector('[data-action="close"]')!.addEventListener('click', handlers.onClose);
    window.addEventListener('keydown', this.onKeyboardCheckDown);
    window.addEventListener('keyup', this.onKeyboardCheckUp);
    window.addEventListener('blur', this.resetKeyboardCheck);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.resetKeyboardCheck();
    });
    attachMenuSounds(this.root);

    parent.appendChild(this.root);
  }

  show(): void {
    this.refreshAudioRows();
    this.refreshMotionBlur();
    this.resetKeyboardCheck();
    this.root.hidden = false;
  }
  hide(): void {
    this.root.hidden = true;
    this.resetKeyboardCheck();
  }

  private refreshAudioRows: () => void = () => {};
  private refreshMotionBlur: () => void = () => {};

  private onKeyboardCheckDown = (e: KeyboardEvent): void => {
    if (this.root.hidden) return;
    this.pressedKeys.add(e.code);
    this.refreshKeyboardCheck();
  };

  private onKeyboardCheckUp = (e: KeyboardEvent): void => {
    if (this.root.hidden) return;
    this.pressedKeys.delete(e.code);
    this.refreshKeyboardCheck();
  };

  private resetKeyboardCheck = (): void => {
    this.pressedKeys.clear();
    this.refreshKeyboardCheck();
  };

  private refreshKeyboardCheck(): void {
    const throttle = Array.from(this.pressedKeys).some((code) => isThrottleKey(code));
    const brake = Array.from(this.pressedKeys).some((code) => isBrakeKey(code));
    const left = this.pressedKeys.has('KeyA') || this.pressedKeys.has('ArrowLeft');
    const right = this.pressedKeys.has('KeyD') || this.pressedKeys.has('ArrowRight');
    const status = (ok: boolean, fallback: string): string => {
      if (ok) return 'OK';
      return fallback;
    };
    this.comboLeftEl.textContent = status(throttle && brake && left, 'Hold W + A + Space');
    this.comboRightEl.textContent = status(throttle && brake && right, 'Hold W + D + Space');
    this.comboLeftEl.classList.toggle('is-ok', throttle && brake && left);
    this.comboRightEl.classList.toggle('is-ok', throttle && brake && right);
  }
}

function reloadWithoutRendererOverrideParams(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('webgl');
  url.searchParams.delete('webgpu');
  window.location.replace(url.toString());
}
